import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Phone, ArrowRight, Sparkles, MapPin, Star } from "lucide-react";
import { BUSINESS, HERO_IMAGE, HERO_VIDEOS } from "@/data/site";

export const Hero = () => {
  const videoRef = useRef(null);
  const [index, setIndex] = useState(0);

  // Cycle through the cleaning clips so the full background keeps changing.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.load();
    const play = v.play();
    if (play && play.catch) play.catch(() => {});
  }, [index]);

  const handleEnded = () => setIndex((i) => (i + 1) % HERO_VIDEOS.length);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center pt-28 pb-16 overflow-hidden"
    >
      {/* Full-screen rotating cleaning video background */}
      <div className="absolute inset-0 z-0">
        <video
          ref={videoRef}
          data-testid="hero-video"
          autoPlay
          muted
          playsInline
          onEnded={handleEnded}
          poster={HERO_IMAGE}
          className="h-full w-full object-cover"
        >
          <source src={HERO_VIDEOS[index]} type="video/mp4" />
        </video>
        {/* Dark gradient overlays keep the headline crisp while the video stays visible */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1a3c]/95 via-[#0a1a3c]/80 to-[#0a1a3c]/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1a3c]/90 via-[#0a1a3c]/20 to-[#0a1a3c]/40" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 w-full">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur px-4 py-1.5 text-sm font-semibold text-sky-100">
            <Sparkles className="h-4 w-4 text-sky-300" />
            Proudly serving Iqaluit and the territory of Nunavut
          </div>

          <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05] drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)]">
            A cleaner, healthier space —{" "}
            <span className="text-sky-300">done right</span> in the North.
          </h1>

          <p className="mt-6 text-lg leading-relaxed text-slate-100 max-w-xl drop-shadow-[0_1px_8px_rgba(0,0,0,0.5)]">
            Reliable, professional and detail-oriented residential &amp;
            commercial cleaning. From move-in/move-out deep cleans to scheduled
            office care — we make your space welcoming, safe and spotless.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <a
              href="#quote"
              data-testid="hero-quote-button"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2563eb] px-7 py-4 text-base font-semibold text-white hover:bg-[#1d4ed8] transition-colors shadow-lg shadow-blue-900/40"
            >
              Request a Free Quote
              <ArrowRight className="h-5 w-5" />
            </a>
            <a
              href={BUSINESS.phoneHref}
              data-testid="hero-call-button"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/70 bg-white/5 backdrop-blur px-7 py-4 text-base font-semibold text-white hover:bg-white hover:text-[#0f2452] transition-colors"
            >
              <Phone className="h-5 w-5" />
              Call {BUSINESS.phone}
            </a>
          </div>

          <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-100">
            <span className="inline-flex items-center gap-2 font-medium">
              <span className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="h-4 w-4 fill-amber-400 text-amber-400"
                  />
                ))}
              </span>
              5-star rated local service
            </span>
            <span className="inline-flex items-center gap-2 font-medium">
              <MapPin className="h-4 w-4 text-sky-300" />
              902 Nunavut Dr, Iqaluit
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
